import { Injectable, OnDestroy, computed, inject, signal } from '@angular/core';
import { SseEventPayload, SseEventKind, isHeartbeatEvent, isSseEventKind } from './sse-event.types';
import { BaseEventDto } from '@tourfold/tf-api-client-frontend-v2';
import { Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, skip } from 'rxjs/operators';
import { SpaConfig } from '@dispo-shared/spa-config/spa-config';
import { EventSourceMessage, fetchEventSource } from '@microsoft/fetch-event-source';
import { AuthService } from '../auth.service';
import { Store } from '@ngrx/store';
import { AuthState } from '../../store/reducers/auth.reducer';
import { selectAuthState } from '../../store/selectors/auth.selector';
import { Login, SetStatus } from '../../store/actions/auth.action';
import { Status } from '../../interfaces/status.enum';
import { LoginResponse } from '../../interfaces/auth.interface';
import { AuthUserResponse } from '@dispo-shared/open-api/models';

const SSE_ENDPOINT = '/frontend/v2/events/stream';
const RECONNECT_MAX_ATTEMPTS = 5;
const RECONNECT_BASE_DELAY = 1000;
const RECONNECT_MAX_DELAY = 90000;
const HEARTBEAT_INTERVAL_MS = 90000;
const HEARTBEAT_GRACE_MS = 15000;

@Injectable({ providedIn: 'root' })
export class SseV2Service implements OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly authStore = inject<Store<AuthState>>(Store);

  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private cancelStream: (() => void) | null = null;
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  private authSubscription: Subscription | null = null;
  private refreshInProgress = false;

  private readonly lastEventId = signal<string | null>(null);
  private readonly connectionStateSignal = signal<'disconnected' | 'connecting' | 'connected' | 'reconnecting'>('disconnected');

  private readonly eventSubjects: Record<SseEventKind, Subject<BaseEventDto>> = this.createEventSubjects();

  readonly connectionState = this.connectionStateSignal.asReadonly();
  readonly isConnected = computed(() => this.connectionStateSignal() === 'connected');

  constructor() {
    this.initializeAuthSubscription();
  }

  /**
   * Subscribes to auth state changes to detect token refresh events.
   * When the access token changes (e.g., after token refresh by HTTP interceptor),
   * automatically reconnects SSE with the new token.
   */
  private initializeAuthSubscription(): void {
    this.authSubscription = this.authStore
      .select(selectAuthState)
      .pipe(
        map((state) => state.tokens?.access_token?.value),
        distinctUntilChanged(),
        skip(1), // Skip initial value
        filter((token): token is string => token != null && token.length > 0)
      )
      .subscribe((token) => {
        console.debug('[SSE] access token updated, reconnecting with fresh token');

        // Only reconnect if we were previously connected or in an error state
        const currentState = this.connectionStateSignal();
        if (currentState === 'connected' || currentState === 'reconnecting') {
          console.debug('[SSE] disconnecting old connection before reconnecting');
          this.disconnect();

          // Reset reconnect attempts since we have a fresh token
          this.reconnectAttempts = 0;

          // Reconnect with new token after a short delay to ensure clean disconnect
          setTimeout(() => {
            console.debug('[SSE] reconnecting with refreshed token');
            this.connect();
          }, 100);
        }
      });
  }

  connect(): void {
    if (this.connectionStateSignal() === 'connected' || this.connectionStateSignal() === 'connecting') {
      console.debug('[SSE] connect called but already in state', this.connectionStateSignal());
      return;
    }

    this.connectionStateSignal.set('connecting');
    this.clearReconnectTimer();

    const rootUrl = SpaConfig.apiUrlProvider.rootUrl ?? window.location.origin;
    const lastId = this.lastEventId();
    const url = `${rootUrl}${SSE_ENDPOINT}${lastId ? `?lastEventId=${encodeURIComponent(lastId)}` : ''}`;

    console.debug('[SSE] connecting to', url);

    const controller = new AbortController();
    this.cancelStream = () => controller.abort();

    const accessToken = this.authService.getAccessToken();

    fetchEventSource(url, {
      signal: controller.signal,
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      openWhenHidden: true,
      onopen: async (response: Response) => {
        if (response.status === 401 || response.status === 403) {
          console.warn('[SSE] auth error on open, triggering token refresh');
          // Pin attempts at max so scheduleReconnect skips — handleAuthError owns the reconnect
          this.reconnectAttempts = RECONNECT_MAX_ATTEMPTS;
          this.handleAuthError();
          throw new Error(`SSE auth error: ${response.status}`);
        }
        if (!response.ok) {
          console.error('[SSE] connection failed with status', response.status);
          throw new Error(`SSE connection failed: ${response.status}`);
        }
        console.debug('[SSE] connection opened');
        this.connectionStateSignal.set('connected');
        this.reconnectAttempts = 0;
        this.resetHeartbeatTimer();
      },
      onmessage: (event: EventSourceMessage) => this.handleStreamMessage(event),
      onclose: () => {
        console.debug('[SSE] connection closed by server');
        this.handleStreamTermination(false);
      },
      onerror: (error: unknown) => {
        console.error('[SSE] connection error', error);
        this.connectionStateSignal.set('disconnected');
        this.clearHeartbeatTimer();
        // Throw to stop fetchEventSource's built-in retry mechanism.
        // Auth errors (401) are handled in onopen; .catch() handles reconnect scheduling.
        throw error;
      },
    }).catch((error: unknown) => {
      console.error('[SSE] fetch error', error);
      this.handleStreamTermination(true);
    }).finally(() => {
      if (this.connectionStateSignal() !== 'connected') {
        console.debug('[SSE] finalizing in state', this.connectionStateSignal());
        this.connectionStateSignal.set('disconnected');
      }
    });
  }

  disconnect(): void {
    console.debug('[SSE] disconnect invoked');
    this.connectionStateSignal.set('disconnected');
    this.clearReconnectTimer();
    this.cancelStream?.();
    this.cancelStream = null;
    this.clearHeartbeatTimer();
  }

  on<K extends SseEventKind>(kind: K): Observable<SseEventPayload<K>> {
    return this.eventSubjects[kind].asObservable() as Observable<SseEventPayload<K>>;
  }

  private handleStreamMessage(event: EventSourceMessage): void {
    if (!event.data) {
      console.debug('[SSE] empty message received');
      return;
    }

    const payload = JSON.parse(event.data) as BaseEventDto;
    const enrichedPayload: BaseEventDto = {
      ...payload,
      kind: payload.kind ?? (event.event as SseEventKind | undefined),
      eventId: payload.eventId ?? event.id ?? this.lastEventId(),
    };

    console.debug('[SSE] event received', enrichedPayload.kind, enrichedPayload.eventId);

    this.handleIncomingEvent(enrichedPayload);
    if (enrichedPayload.kind === 'HEARTBEAT:V1') {
      this.resetHeartbeatTimer();
    }
  }

  private handleIncomingEvent(payload: BaseEventDto): void {
    if (payload.eventId && !isHeartbeatEvent(payload)) {
      this.lastEventId.set(payload.eventId);
    }

    if (isHeartbeatEvent(payload)) {
      this.connectionStateSignal.set('connected');
      this.reconnectAttempts = 0;
      return;
    }

    if (!isSseEventKind(payload.kind)) {
      console.warn('[SSE] unrecognized event kind', payload.kind);
      return;
    }

    if (this.connectionStateSignal() !== 'connected') {
      this.connectionStateSignal.set('connected');
      this.reconnectAttempts = 0;
    }

    this.eventSubjects[payload.kind].next(payload);
  }

  private handleStreamTermination(isError: boolean): void {
    console.debug('[SSE] handleStreamTermination', { isError });
    this.cancelStream?.();
    this.cancelStream = null;
    this.clearHeartbeatTimer();

    if (this.connectionStateSignal() === 'connected' && !isError) {
      this.connectionStateSignal.set('reconnecting');
      this.scheduleReconnect();
    } else if (isError) {
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || this.reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) {
      console.warn('[SSE] reconnect skipped', {
        reconnectTimer: !!this.reconnectTimer,
        reconnectAttempts: this.reconnectAttempts,
      });
      return;
    }

    this.reconnectAttempts += 1;
    this.connectionStateSignal.set('reconnecting');

    const delay = Math.min(
      RECONNECT_BASE_DELAY * Math.pow(2, this.reconnectAttempts - 1),
      RECONNECT_MAX_DELAY
    );

    console.debug('[SSE] scheduling reconnect', { delay, reconnectAttempts: this.reconnectAttempts });

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      console.debug('[SSE] reconnecting now');
      this.connect();
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private resetHeartbeatTimer(): void {
    this.clearHeartbeatTimer();

    this.heartbeatTimer = setTimeout(() => {
      console.warn('[SSE] heartbeat watchdog triggered');
      this.handleStreamTermination(true);
    }, HEARTBEAT_INTERVAL_MS + HEARTBEAT_GRACE_MS);
  }

  private clearHeartbeatTimer(): void {
    if (this.heartbeatTimer) {
      clearTimeout(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private createEventSubjects(): Record<SseEventKind, Subject<BaseEventDto>> {
    return {
      'CASE_CREATED:V1': new Subject<BaseEventDto>(),
      'CASE_UPDATED:V1': new Subject<BaseEventDto>(),
      'CASE_ACTION:V1': new Subject<BaseEventDto>(),
      'CASE_COMMENT_ADDED:V1': new Subject<BaseEventDto>(),
      'CASE_EXTERNAL_ID_UPDATED:V1': new Subject<BaseEventDto>(),
      'TOUR_CREATED:V1': new Subject<BaseEventDto>(),
      'TOUR_UPDATED:V1': new Subject<BaseEventDto>(),
      'TOUR_DELETED:V1': new Subject<BaseEventDto>(),
      'LOCATION_UPDATED:V1': new Subject<BaseEventDto>(),
      'VEHICLE_LOCATION_UPDATED:V1': new Subject<BaseEventDto>(),
      'AREA_WAITING_TIME_UPDATED:V1': new Subject<BaseEventDto>(),
      'HEARTBEAT:V1': new Subject<BaseEventDto>(),
    };
  }

  /**
   * Handles 401 auth errors by refreshing the access token and reconnecting directly.
   * Bypasses the initializeAuthSubscription guard (which requires 'reconnecting' state)
   * so that reconnection works even when .finally() has already set state to 'disconnected'.
   * A refreshInProgress flag prevents parallel refresh calls from multiple error triggers.
   */
  private handleAuthError(): void {
    if (this.refreshInProgress) {
      console.debug('[SSE] token refresh already in progress, skipping duplicate call');
      return;
    }

    const refreshToken = this.authService.getRefreshToken();
    if (!refreshToken) {
      console.warn('[SSE] no refresh token available, cannot recover SSE');
      return;
    }

    this.refreshInProgress = true;
    console.debug('[SSE] refreshing access token');

    this.authService.refreshAccessToken().subscribe({
      next: (response: AuthUserResponse | string) => {
        this.refreshInProgress = false;
        if (
          typeof response !== 'string' &&
          response.access_token?.value &&
          response.access_token?.valid_until &&
          response.refresh_token?.value &&
          response.refresh_token?.valid_until &&
          response.id
        ) {
          console.debug('[SSE] token refresh successful, reconnecting SSE');
          this.authStore.dispatch(
            new Login({ tokens: response as LoginResponse, refresh: false })
          );
          this.authStore.dispatch(new SetStatus({ status: Status.Success }));
          // Reconnect directly — the new token is now in localStorage and will be
          // picked up by getAccessToken() inside the next connect() call.
          this.reconnectAttempts = 0;
          setTimeout(() => this.connect(), 100);
        } else {
          console.error('[SSE] invalid token refresh response, SSE will remain disconnected');
        }
      },
      error: (err) => {
        this.refreshInProgress = false;
        console.error('[SSE] token refresh failed, SSE will remain disconnected', err);
      },
    });
  }

  ngOnDestroy(): void {
    console.debug('[SSE] service destroyed, cleaning up');

    // Unsubscribe from auth state changes
    this.authSubscription?.unsubscribe();
    this.authSubscription = null;

    // Disconnect SSE connection
    this.disconnect();

    // Complete all event subjects
    Object.values(this.eventSubjects).forEach((subject) => subject.complete());
  }
}
